import React from 'react'
import { useSelector } from 'react-redux'
import { Route, Switch } from 'react-router'
import AccountsAccounts from 'src/app/AccountsAccounts/AccountsAccounts'
import AccountsBudgets from 'src/app/AccountsBudgets/AccountsBudgets'
import AccountsRequests from 'src/app/AccountsRequests/AccountsRequests'
import AccountsVirtualCards from 'src/app/AccountsVirtualCards/AccountsVirtualCards'
import BackgroundPage from 'src/components/BackgroundPage/BackgroundPage'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import TitleHeader from 'src/components/TitleHeader/TitleHeader'
import { useTranslate } from 'src/localization/Trns'
import { Rdrct } from 'src/navigation/Lnk'
import {
  accountsAccountsUrl,
  accountsBudgetsUrl,
  accountsRequestsUrl,
  accountsUrl,
  accountsVirtualCardsUrl,
} from 'src/navigation/urls'
import { useQueryStatistics } from 'src/queries/statistics/statistics.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'

/**
 * AccountsFamilyPage — route-level page for the Accounts family (EX-39923,
 * supersedes #6986). Reproduces the legacy `src/app/Accounts/Accounts` shell.
 *
 * Parity: the Accounts family is NOT a tabbed surface — this shell renders a
 * "Physical Cards" title with NO tab bar (do NOT introduce a FamilyLayout tab
 * bar; it would change UX). The family exists only to feed the flat gate +
 * metadata; the 4 family content components each own their own layout/content.
 *
 * Gate: registered once for the whole family (see flatArchitectureRoutes.tsx),
 * so this page stays mounted across the family paths. The nested
 * `<Switch location={calculatedBackgroundLocation}>` swaps the routed content.
 */
const AccountsFamilyPage: React.FC = () => {
  const translate = useTranslate()

  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const { results: statisticsResults } = useQueryStatistics({
    statisticsKeys: [
      StatisticsKeys.CreditCardsSourceBudgetsEnabledCount,
      StatisticsKeys.CreditCardsDelegateCount,
      StatisticsKeys.CreditCardsSourceCount,
    ] as const,
  })
  const creditCardsSourceCount =
    statisticsResults[StatisticsKeys.CreditCardsSourceCount].data ?? 0

  return !(
    statisticsResults[StatisticsKeys.CreditCardsSourceBudgetsEnabledCount]
      .isSuccess &&
    statisticsResults[StatisticsKeys.CreditCardsDelegateCount].isSuccess &&
    statisticsResults[StatisticsKeys.CreditCardsSourceCount].isSuccess
  ) ? (
    <LoadingBackgroundPage />
  ) : (
    <BackgroundPage>
      <TitleHeader title={translate('Physical Cards')} />
      <Switch location={calculatedBackgroundLocation}>
        <Route exact path={accountsAccountsUrl()}>
          <AccountsAccounts />
        </Route>
        <Route exact path={accountsBudgetsUrl()}>
          <AccountsBudgets />
        </Route>
        <Route exact path={accountsVirtualCardsUrl()}>
          <AccountsVirtualCards />
        </Route>
        <Route exact path={accountsRequestsUrl()}>
          <AccountsRequests />
        </Route>
        <Route exact path={accountsUrl()}>
          <Rdrct
            to={
              creditCardsSourceCount > 0
                ? accountsAccountsUrl()
                : accountsBudgetsUrl()
            }
          />
        </Route>
        <Route path={'/'}>
          <NotFoundBackgroundPageContent />
        </Route>
      </Switch>
    </BackgroundPage>
  )
}

export default AccountsFamilyPage
